import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const HERO_BG = "https://images.pexels.com/photos/6949994/pexels-photo-6949994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Africa-Russia Strategic Connection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-oar-blue/90 via-oar-blue/70 to-oar-bg-secondary" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-64 h-64 border border-white/20 rounded-full opacity-50" />
      <div className="absolute bottom-1/4 right-10 w-96 h-96 border border-oar-orange/20 rounded-full opacity-50" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
        {/* Overline */}
        <div className="flex items-center justify-center gap-3 mb-8 animate-fade-in">
          <Globe2 className="w-5 h-5 text-oar-orange" />
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-white font-bold">
            {t('hero.overline')}
          </span>
        </div>

        {/* Main Title */}
        <h1
          data-testid="hero-title"
          className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none text-white mb-6 animate-fade-in animate-delay-100"
        >
          <span className="block">OAR</span>
          <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-4 font-normal text-oar-orange">
            {t('hero.title')}
          </span>
        </h1>

        {/* Subtitle */}
        <p
          data-testid="hero-subtitle"
          className="text-lg md:text-xl text-white/90 tracking-widest uppercase mb-8 animate-fade-in animate-delay-200"
        >
          {t('hero.subtitle')}
        </p>

        {/* Description */}
        <p className="max-w-3xl mx-auto text-base md:text-lg text-white/80 leading-relaxed mb-12 animate-fade-in animate-delay-300">
          {t('hero.description')}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in animate-delay-400">
          <Link
            to="/programs"
            data-testid="hero-cta-primary"
            className="group flex items-center gap-2 bg-oar-red hover:bg-oar-red-dark text-white rounded-sm px-8 py-4 font-medium transition-all duration-300 shadow-lg"
          >
            {t('hero.cta_primary')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/membership"
            data-testid="hero-cta-secondary"
            className="flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-oar-blue rounded-sm px-8 py-4 transition-all duration-300"
          >
            {t('hero.cta_secondary')}
          </Link>
        </div>

        {/* African Colors Bar */}
        <div className="mt-24 animate-fade-in animate-delay-500">
          <div className="flex justify-center gap-2">
            <div className="w-16 h-1 bg-oar-green rounded-full" />
            <div className="w-16 h-1 bg-oar-orange rounded-full" />
            <div className="w-16 h-1 bg-oar-green rounded-full" />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white rounded-full" />
        </div>
      </div>
    </section>
  );
};
